import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

type ResetForm = { newPassword: string; confirmPassword: string };

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ResetForm>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ResetForm) => {
    if (!token) {
      toast.error('Enlace inválido o incompleto.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await authAPI.resetPassword({ token, newPassword: data.newPassword });
      toast.success(res.data?.message || 'Contraseña actualizada.');
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'No se pudo restablecer la contraseña.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-10 max-w-md text-center space-y-4">
          <p className="text-slate-700">Este enlace no es válido o ha caducado.</p>
          <Link to="/login" className="text-slate-600 underline text-sm">
            Ir al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-10 lg:p-12">
          <div className="mb-10">
            <h2 className="text-3xl font-light text-slate-800 tracking-tight mb-2">
              Nueva contraseña
            </h2>
            <p className="text-sm text-slate-500 font-light">
              Elige una contraseña segura (mínimo 6 caracteres).
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nueva contraseña
              </label>
              <div className="relative group">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                    passwordFocused ? 'text-slate-600' : 'text-slate-400'
                  }`}
                >
                  <LockClosedIcon className="h-5 w-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className={`block w-full pl-12 pr-12 py-3.5 text-base bg-white border rounded-xl transition-all duration-300 placeholder-slate-400 focus:outline-none ${
                    errors.newPassword
                      ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : passwordFocused
                        ? 'border-slate-400 shadow-md shadow-slate-200/50 ring-2 ring-slate-200/50'
                        : 'border-slate-300 hover:border-slate-400'
                  }`}
                  {...register('newPassword', {
                    required: 'La contraseña es requerida',
                    minLength: { value: 6, message: 'Mínimo 6 caracteres' }
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-sm text-red-600 mt-1">{errors.newPassword.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirmar contraseña
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`block w-full px-4 py-3.5 text-base bg-white border rounded-xl focus:outline-none ${
                  errors.confirmPassword ? 'border-red-400' : 'border-slate-300'
                }`}
                {...register('confirmPassword', {
                  required: 'Confirma tu contraseña',
                  validate: (v) => v === newPassword || 'Las contraseñas no coinciden'
                })}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center px-6 py-4 text-base font-medium text-white bg-slate-700 rounded-xl shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50"
              >
                {isLoading ? 'Guardando…' : 'Guardar contraseña'}
              </button>
            </div>

            <div className="text-center pt-2">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors duration-200 font-light"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
